import { Fragment, h } from '@stencil/core';
import cn from 'classnames';
import { parseCommonURLToRouterParts, getConsumerSpecificData, isIndexDBEnabled, isPBS, indexDBBreadcrumbs, filterBreadcrumbsForSaving, tryParse, setupMedalliaSurvey, PageNames } from '@pes-ui/utils';
import { isAppRoutingEnabled, navigateWithRouter } from '../pes-router/utils';
import PesRoute from '../pes-router/pes-route';
import { getProductInfoFromBreadcrumbs } from './utils';
export class Breadcrumbs {
  constructor() {
    this.handleNavigate = (route, breadcrumb) => {
      return async (event) => {
        event.preventDefault();
        const target = event.currentTarget;
        await this.handleClickBreadcrumb(event, breadcrumb);
        navigateWithRouter(route, target);
      };
    };
    this.saveBreadcrumbsInIndexDB = async (storageKey, breadcrumbsToStore, clickedBreadcrumb) => {
      try {
        await indexDBBreadcrumbs.createOrUpdate({
          storageName: this.storageName,
          storageKey,
          updatedBreadCrumbs: breadcrumbsToStore,
        });
        this.breadcrumbsFromStorage = breadcrumbsToStore;
      }
      catch (e) {
        console.error(`[PES]: Breadcrumbs can not be saved or updated in indexDB ${this.storageName} with ${storageKey} key`, e);
      }
      if (!isAppRoutingEnabled()) {
        window.location.href = clickedBreadcrumb.url;
      }
    };
    this.handleClickBreadcrumb = async (event, clickedBreadcrumb) => {
      var _a;
      event.preventDefault();
      const storageKey = `${clickedBreadcrumb.itemType}/${clickedBreadcrumb.id}`;
      const currentBreadcrumbs = ((_a = this.breadcrumbsFromStorage) === null || _a === void 0 ? void 0 : _a.length)
        ? this.breadcrumbsFromStorage
        : this.breadcrumbs;
      const breadcrumbsToStore = filterBreadcrumbsForSaving(currentBreadcrumbs, clickedBreadcrumb.itemType);
      await this.saveBreadcrumbsInIndexDB(storageKey, breadcrumbsToStore, clickedBreadcrumb);
    };
    this.getBreadcrumb = (breadcrumb) => {
      let breadcrumbProps = {};
      if (breadcrumb.url && breadcrumb.url !== '#') {
        if (isAppRoutingEnabled()) {
          const { pageType, pageId } = parseCommonURLToRouterParts(breadcrumb.url);
          const route = new PesRoute({ page: pageType, pageId });
          breadcrumbProps = {
            href: route.url,
            onDidNavigate: this.handleNavigate(route, breadcrumb),
          };
        }
        else {
          breadcrumbProps = {
            href: breadcrumb.url,
            onClick: isIndexDBEnabled() ? (e) => this.handleClickBreadcrumb(e, breadcrumb) : undefined,
          };
        }
      }
      return (h("se-breadcrumb-item", Object.assign({}, breadcrumbProps), h("span", { innerHTML: breadcrumb.name })));
    };
    this.breadcrumbs = undefined;
    this._breadcrumbs = undefined;
    this.hideOnMobile = false;
    this._hideOnMobile = undefined;
    this.hostWithLocale = undefined;
    this.shouldSetupMedalliaSurvey = false;
    this.tail = [];
    this.breadcrumbsFromStorage = [];
    this.storageName = '';
    this.el.dir = document.documentElement.dir || 'auto';
  }
  handleTailDidAppend(event) {
    this.setTailState(event.detail);
  }
  setTailState(data) {
    this.tail = (data === null || data === void 0 ? void 0 : data.length) ? [...data] : [];
  }
  async componentWillLoad() {
    var _a, _b, _c;
    this.breadcrumbs = (_a = tryParse(this._breadcrumbs)) !== null && _a !== void 0 ? _a : this.breadcrumbs;
    this.hideOnMobile = (_b = tryParse(this._hideOnMobile)) !== null && _b !== void 0 ? _b : this.hideOnMobile;
    if (!isIndexDBEnabled())
      return;
    const { consumer, storageName } = getConsumerSpecificData(isPBS(), this.hostWithLocale);
    this.storageName = storageName;
    const lastBreadcrumb = ((_c = this.breadcrumbs) === null || _c === void 0 ? void 0 : _c.length)
      ? this.breadcrumbs[this.breadcrumbs.length - 1]
      : {};
    const pageType = lastBreadcrumb.itemType;
    const pageId = lastBreadcrumb.id;
    const pageKey = `${pageType}/${pageId}`;
    indexDBBreadcrumbs.setChannel(consumer);
    try {
      const fetchedBreadcrumbs = await indexDBBreadcrumbs.setupStorage({
        breadcrumbs: this.breadcrumbs,
        storageName,
        pageKey,
      });
      if (!(fetchedBreadcrumbs === null || fetchedBreadcrumbs === void 0 ? void 0 : fetchedBreadcrumbs.length)) {
        this.breadcrumbsFromStorage = this.breadcrumbs;
      }
      this.breadcrumbsFromStorage = fetchedBreadcrumbs;
      if (!this.shouldSetupMedalliaSurvey)
        return;
      const rangeBreadcrumb = this.breadcrumbsFromStorage.find(({ itemType }) => itemType === PageNames.RANGE);
      if (!rangeBreadcrumb)
        return;
      console.info(`[PES]: Setting up range ${rangeBreadcrumb.id} for medallia survey from indexDB storage`);
      setupMedalliaSurvey(rangeBreadcrumb.id);
    }
    catch (e) {
      console.error(`[PES]: Breadcrumbs can not be updated in indexDB ${storageName} storage`, e);
    }
  }
  render() {
    var _a, _b;
    if (!((_a = this.breadcrumbs) === null || _a === void 0 ? void 0 : _a.length)) {
      return null;
    }
    const breadcrumbsList = ((_b = this.breadcrumbsFromStorage) === null || _b === void 0 ? void 0 : _b.length)
      ? this.breadcrumbsFromStorage
      : this.breadcrumbs;
    const breadcrumbs = breadcrumbsList.concat(this.tail);
    const { productCategory, productSubCategory, productRange, productName, } = getProductInfoFromBreadcrumbs(this.breadcrumbs);
    return (h(Fragment, null, h("se-breadcrumb", { class: cn('breadcrumbs', 'js-pes-sticky', {
        'breadcrumbs--hide-mobile': this.hideOnMobile,
      }), breakpoint: "desktop", withMicrodata: "true" }, breadcrumbs.map(this.getBreadcrumb)), h("pes-product-info-chat-event", { productCategory: productCategory, productSubCategory: productSubCategory, productRange: productRange, productName: productName, sendProductInfoUsingInterval: true })));
  }
  static get is() { return "pes-breadcrumbs"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["breadcrumbs.scss"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["breadcrumbs.css"]
    };
  }
  static get properties() {
    return {
      "breadcrumbs": {
        "type": "unknown",
        "mutable": true,
        "complexType": {
          "original": "TBreadcrumbs[]",
          "resolved": "TBreadcrumbs[]",
          "references": {
            "TBreadcrumbs": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "breadcrumbs data"
        }
      },
      "_breadcrumbs": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-breadcrumbs",
        "reflect": false
      },
      "hideOnMobile": {
        "type": "boolean",
        "mutable": true,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "configures if breadcrumbs should be hidden on mobile screen"
        },
        "attribute": "hide-on-mobile",
        "reflect": false,
        "defaultValue": "false"
      },
      "_hideOnMobile": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "plain-hide-on-mobile",
        "reflect": false
      },
      "hostWithLocale": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "hostWithLocale contains host, countryCode and languageCode"
        },
        "attribute": "host-with-locale",
        "reflect": false
      },
      "shouldSetupMedalliaSurvey": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "should-setup-medallia-survey",
        "reflect": false,
        "defaultValue": "false"
      }
    };
  }
  static get states() {
    return {
      "tail": {},
      "breadcrumbsFromStorage": {},
      "storageName": {}
    };
  }
  static get elementRef() { return "el"; }
  static get listeners() {
    return [{
        "name": "pesBreadcrumbsTailDidAppend",
        "method": "handleTailDidAppend",
        "target": "document",
        "capture": false,
        "passive": false
      }];
  }
}
